import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchInstalledBaseData, fetchAssetDetails } from "../Services/InstalledBaseThunk";
import { setProp } from "Store/Actions";
import { setPropT } from "Store/Actions/ts";
import setObj from "lodash/set";
import { Network } from "inspector/promises";

interface InstalledBaseItem {
  network_id: string;
  network_status: string;
  network_name?: string;
  slice_id: string;
  product_id: string;
  product_description: string;
  sold_qty: number | null;
  installed_qty: number | null;
  product_lifecycle_status: string;
  product_family_id: string;
  successor_replacement_product_id: string | null;
  reconciliation_status: string;
}

interface AssetDetailItem {
  network_id: string;
  network_status: string;
  network_name: string;
  slice_id: string;
  product_id: string;
  product_description: string;
  dc_hardware_installed_base_serial_number: string;
  registered_product_details_serial_number: string;
  product_lifecycle_status: string;
  product_family_id: string;
  successor_replacement_product_id: string | null;
}

interface SummaryData {
  total: number;
  matched: number;
  mismatch: number;
  partially_matched: number;
}

interface PartnerItem {
  id: string;
  value: string;
}

interface CustomerItem {
  id: string;
  value: string;
  apiResponse?: any;
  partnerId?: string;
}

interface NetworkItem {
  id: string;
  value: string;
  slice_id: string | null;
}

interface FilterSelection {
  partner: string;
  customer: string;
  network: string[];
}

interface AccountInfo {
  sellToPartyName: string;
  endCustomerName: string;
}


interface InstalledBaseState {
  loading: boolean;
  hasSearched: boolean;
  showContainer: boolean;
  activeTab: 'hardware' | 'software' | 'application';
  data: InstalledBaseItem[];
  summary: SummaryData;
  error: string | null;
  accountDropdown: any[];
  accountInfo: AccountInfo;
  selectedNetworkData: any;

  // Filter state
  filters: {
    selection: FilterSelection;
  };

  // List data
  isPartnerLoading: boolean;
  isCustomerLoading: boolean;
  isNetworkLoading: boolean;
  partnerList: PartnerItem[];
  customerList: CustomerItem[];
  networkList: NetworkItem[];
  snackbarMessage: string;

  // Asset details state
  assetDetails: {
    loading: boolean;
    data: AssetDetailItem[];
    error: string | null;
    selectedProductId: string;
    clearGridSelection: boolean;
    columnFilter: Record<string, any>;
    showColumnDialog?: boolean;  // Add this
    activeColumn?: string;
    totalCount: number;      // ← ADD THIS
    pageCount: number;       // ← ADD THIS 
  };

  reassignDetails: {
    loading: boolean,
    networkDetails: any,
    reassignConfirmStatus: boolean,
    previous_network_id: string,
    new_network_id: string,
    ressignBlockStatus: boolean,
    selectedNetwork: string
  };

  dataLoading: boolean;
}

const initialState: InstalledBaseState = {
  loading: false,
  hasSearched: false,
  showContainer: false,
  activeTab: 'hardware',
  data: [],
  summary: {
    total: 0,
    matched: 0,
    mismatch: 0,
    partially_matched: 0,
  },
  error: null,
  accountDropdown: [],
  accountInfo: {  // Add this
    sellToPartyName: "",
    endCustomerName: "",
  },
  selectedNetworkData: {},

  // Filter state
  filters: {
    selection: {
      partner: "",
      customer: "",
      network: [],
    },
  },

  // List data
  isPartnerLoading: false,
  isCustomerLoading: false,
  isNetworkLoading: false,
  partnerList: [],
  customerList: [],
  networkList: [],
  snackbarMessage: "",

  // Asset details state
  assetDetails: {
    loading: false,
    data: [],
    error: null,
    selectedProductId: '',
    clearGridSelection: false,
    showColumnDialog: false,  // Add this
    activeColumn: '',
    columnFilter: {
      network_id: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      },
      network_status: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      },
      product_id: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      },
      product_description: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      },
      dc_hardware_installed_base_serial_number: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      },
      registered_product_details_serial_number: {
        filter: "",
        sort: "",
        checkempty: false,
        filterCheck: false,
        filterShow: true,  // ← ADD THIS
        filterQuery: "like",
        dropDownList: []
      }
    },
    totalCount: 0,      // ← ADD THIS
    pageCount: 0,       // ← ADD THIS

  },
  reassignDetails: {
    loading: false,
    networkDetails: {},
    reassignConfirmStatus: false,
    previous_network_id: "",
    new_network_id: "",
    ressignBlockStatus: false,
    selectedNetwork: ""
  },
  dataLoading: true
};

const installedBaseSlice = createSlice({
  name: "installedBase",
  initialState,
  reducers: {
    setShowContainer: (state, action: PayloadAction<boolean>) => {
      state.showContainer = action.payload;
    },
    setAccountInfo: (state, action: PayloadAction<Partial<AccountInfo>>) => {
      state.accountInfo = {
        ...state.accountInfo,
        ...action.payload,
      };
    },
    setActiveTab: (state, action: PayloadAction<'hardware' | 'software' | 'application'>) => {
      state.activeTab = action.payload;
    },
    setData: (state, action: PayloadAction<InstalledBaseItem[]>) => {
      state.data = action.payload;
    },
    setSummary: (state, action: PayloadAction<SummaryData>) => {
      state.summary = action.payload;
    },
    resetData: (state) => {
      state.data = [];
      state.summary = {
        total: 0,
        matched: 0,
        mismatch: 0,
        partially_matched: 0,
      };
      state.hasSearched = false;
      state.error = null;
    },

    // Filter actions
    setFilterSelection: (state, action: PayloadAction<Partial<FilterSelection>>) => {
      state.filters.selection = {
        ...state.filters.selection,
        ...action.payload,
      };
    },

    // Loader actions
    setLoader: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
      const { key, value } = action.payload;
      state[key] = value;
    },

    // List actions
    setPartnerList: (state, action: PayloadAction<PartnerItem[]>) => {
      state.partnerList = action.payload;
    },
    setCustomerList: (state, action: PayloadAction<CustomerItem[]>) => {
      state.customerList = action.payload;
    },
    setNetworkList: (state, action: PayloadAction<NetworkItem[]>) => {
      state.networkList = action.payload;
    },
    setAccountDropdown: (state, action: PayloadAction<any[]>) => {
      state.accountDropdown = action.payload;
    },
    // Reset filter
    resetFilters: (state) => {
      state.filters.selection = {
        partner: "",
        customer: "",
        network: [],
      };
      state.showContainer = false;
      state.customerList = [];
      state.networkList = [];
      state.accountInfo = {  // Reset account info too
        sellToPartyName: "",
        endCustomerName: "",
      };
    },

    setSelectedProductId: (state, action: PayloadAction<string>) => {
      state.assetDetails.selectedProductId = action.payload;
    },
    triggerClearSelection: (state) => {
      state.assetDetails.clearGridSelection = !state.assetDetails.clearGridSelection;
    },
    resetAssetDetails: (state) => {
      state.assetDetails = {
        loading: false,
        data: [],
        error: null,
        selectedProductId: '',
        clearGridSelection: false,
        totalCount: 0,      // ← ADD THIS
        pageCount: 0,       // ← ADD THIS
        columnFilter: {
          network_id: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          },
            network_status: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          },
          product_id: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          },
          product_description: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          },
          dc_hardware_installed_base_serial_number: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          },
          registered_product_details_serial_number: {
            filter: "",
            sort: "",
            checkempty: false,
            filterCheck: false,
            filterShow: true,  // ← ADD THIS
            filterQuery: "like",
            dropDownList: []
          }
        }
      };
    },
    setAssetDetailsColumnFilter: (state, action: PayloadAction<{ columnId: string; type: string; value: any }>) => {
      const { columnId, type, value } = action.payload;
      if (!state.assetDetails.columnFilter[columnId]) {
        state.assetDetails.columnFilter[columnId] = {};
      }
      state.assetDetails.columnFilter[columnId][type] = value;
    },
    resetAssetDetailsColumnFilter: (state) => {
      state.assetDetails.columnFilter = {
        network_id: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        },
         network_status: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        },
        product_id: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        },
        product_description: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        },
        dc_hardware_installed_base_serial_number: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        },
        registered_product_details_serial_number: {
          filter: "",
          sort: "",
          checkempty: false,
          filterCheck: false
        }
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInstalledBaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInstalledBaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.hasSearched = true;
        state.data = action.payload.data || [];
        state.summary = action.payload.summary || initialState.summary;
      })
      .addCase(fetchInstalledBaseData.rejected, (state, action) => {
        state.loading = false;
        state.hasSearched = false;
        state.error = action.error.message || "Failed to fetch data";
      })
      // Asset details cases
      .addCase(fetchAssetDetails.pending, (state) => {
        state.assetDetails.loading = true;
        state.assetDetails.error = null;
      })
      .addCase(fetchAssetDetails.fulfilled, (state, action) => {
        state.assetDetails.loading = false;
        state.assetDetails.data = action.payload.data || [];
        state.assetDetails.totalCount = action.payload.totalCount || 0;     // ← ADD
        state.assetDetails.pageCount = action.payload.pageCount || 0;  
        state.assetDetails.selectedProductId = action.payload.product_id;
      })
      .addCase(fetchAssetDetails.rejected, (state, action) => {
        state.assetDetails.loading = false;
        state.assetDetails.error = action.error.message || "Failed to fetch asset details";
      })
      .addCase(setProp, (state, action: setPropT) => {
        if (action.payload.slice === installedBaseSlice.name) {
          setObj(state, action.payload.key, action.payload.value);
        }
      })
  }
});

export const {
  setActiveTab,
  setShowContainer,
  setData,
  setSummary,
  resetData,
  setFilterSelection,
  setLoader,
  setPartnerList,
  setCustomerList,
  setNetworkList,
  resetFilters,
  setAccountDropdown,
  setAccountInfo,
  setSelectedProductId,
  resetAssetDetails,
  triggerClearSelection,
  setAssetDetailsColumnFilter,
  resetAssetDetailsColumnFilter
} = installedBaseSlice.actions;

export default installedBaseSlice.reducer;